import React, { useState, useEffect } from 'react';
import {
  FloatingDrawer,
  FloatingDrawerHeader,
  FloatingDrawerBody,
  FloatingDrawerFooter,
} from '@/components/ui/floating-drawer';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import { Shield, ChevronRight } from 'lucide-react';
import { TeamMember } from '@/lib/api/services/teamService';
import { primaryMenuItems, standaloneNavItems } from '@/components/layout/AppSidebar';
import { isSectionGranted } from '@/lib/menuPermissions.core';
import { useToast } from '@/hooks/use-toast';
import { showApiError } from '@/lib/errors/showApiError';

const GROUP_BY_ID: Record<string, string> = {
  overview: 'General',
  inbox: 'General',
  marketing: 'Marketing',
  'social-media': 'Marketing',
  customers: 'Marketing',
  automation: 'Marketing',
  ads: 'Marketing',
  instagram: 'Marketing',
  youtube: 'Marketing',
  scheduling: 'Operations',
  'appointments-v2': 'Operations',
  store: 'Operations',
  'commerce-v2': 'Operations',
  'website-builder': 'Operations',
  'data-store': 'Operations',
  petpooja: 'Operations',
  reports: 'Analytics',
  'ai-tools': 'Tools',
  settings: 'Admin',
  help: 'Admin',
};

const GROUP_ORDER = ['General', 'Marketing', 'Operations', 'Analytics', 'Tools', 'Admin', 'Other'];

const standaloneIdFromUrl = (url: string): string => {
  if (url === '/home') return 'overview';
  if (url === '/home/inbox') return 'inbox';
  return url;
};

type MenuEntry = { id: string; label: string; group: string };

const MENU_ITEMS: MenuEntry[] = [
  ...standaloneNavItems.map((i) => {
    const id = standaloneIdFromUrl(i.url);
    return { id, label: i.title, group: GROUP_BY_ID[id] || 'Other' };
  }),
  ...primaryMenuItems.map((i) => ({
    id: i.id,
    label: i.title,
    group: GROUP_BY_ID[i.id] || 'Other',
  })),
].filter((i) => i.group !== 'Admin');

const GROUPS = GROUP_ORDER.filter((g) => MENU_ITEMS.some((i) => i.group === g));

const ALL_IDS = new Set(MENU_ITEMS.map(i => i.id));

const expandLegacySectionIds = (perms: string[]): string[] => {
  const next = new Set(perms);
  for (const { id } of MENU_ITEMS) {
    if (isSectionGranted(id, perms)) next.add(id);
  }
  return Array.from(next);
};

interface MemberPermissionsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  member: TeamMember;
  onSave: (memberId: string, menuPermissions: string[] | null) => Promise<void>;
}

export const MemberPermissionsDialog: React.FC<MemberPermissionsDialogProps> = ({
  open,
  onOpenChange,
  member,
  onSave,
}) => {
  const { toast } = useToast();
  const [selected, setSelected] = useState<Set<string>>(new Set(ALL_IDS));
  const [saving, setSaving] = useState(false);

  const getMenuPermissions = (m: TeamMember): string[] | null => {
    let prefs = (m as any).preferences;
    if (typeof prefs === 'string') {
      try { prefs = JSON.parse(prefs); } catch { prefs = null; }
    }
    const mp = prefs?.menu_permissions ?? (m as any).menu_permissions;
    return Array.isArray(mp) ? mp : null;
  };

  useEffect(() => {
    if (open) {
      const perms = getMenuPermissions(member);
      setSelected(perms ? new Set(expandLegacySectionIds(perms)) : new Set(ALL_IDS));
    }
  }, [open, member]);

  const toggle = (id: string) => {
    setSelected(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const toggleGroup = (group: string) => {
    const groupIds = MENU_ITEMS.filter(i => i.group === group).map(i => i.id);
    const allChecked = groupIds.every(id => selected.has(id));
    setSelected(prev => {
      const next = new Set(prev);
      groupIds.forEach(id => allChecked ? next.delete(id) : next.add(id));
      return next;
    });
  };

  const selectAll = () => setSelected(new Set(ALL_IDS));
  const deselectAll = () => setSelected(new Set());

  const [openGroups, setOpenGroups] = useState<Set<string>>(new Set());
  const setGroupOpen = (group: string, next: boolean) => {
    setOpenGroups(prev => {
      const updated = new Set(prev);
      if (next) updated.add(group);
      else updated.delete(group);
      return updated;
    });
  };
  useEffect(() => {
    if (!open) setOpenGroups(new Set());
  }, [open]);

  const selectedSectionCount = MENU_ITEMS.filter(i => selected.has(i.id)).length;

  const allSelected = MENU_ITEMS.every(i => selected.has(i.id));

  const handleSave = async () => {
    if (saving) return;

    const all = Array.from(selected);

    const permissions = all.filter(id => ALL_IDS.has(id));
    const dropped = all.filter(id => !ALL_IDS.has(id));
    if (dropped.length > 0) {
      console.warn('[MemberPermissions] dropping unknown permission ids:', dropped);
    }

    if (permissions.length === 0) {
      toast({
        title: 'Select at least one section',
        description: 'Grant at least one section, or click "Select All" for full access.',
        variant: 'destructive',
      });
      return;
    }

    setSaving(true);
    try {
      await onSave(member.id, permissions);
      onOpenChange(false);
    } catch (error: any) {
      console.error('Error saving permissions:', error);
      showApiError(error, { fallback: { title: "Couldn't save permissions", description: 'Failed to save permissions. Please try again.' } });
    } finally {
      setSaving(false);
    }
  };

  const handleOpenChange = (next: boolean) => {
    if (!next && saving) return;
    onOpenChange(next);
  };

  return (
    <FloatingDrawer
      open={open}
      onOpenChange={handleOpenChange}
      side="right"
      width={480}
      className="overflow-hidden"
    >
      <FloatingDrawerHeader
        title="Menu permissions"
        subtitle={
          <span className="truncate">
            Sidebar sections <strong className="font-semibold text-[#101828]">{member.name}</strong>{' '}
            can access
          </span>
        }
        icon={<Shield className="h-4 w-4" />}
      />

      <FloatingDrawerBody className="space-y-4">
          <div className="flex items-center justify-between">
            <span className="text-xs text-muted-foreground">
              {selectedSectionCount} of {MENU_ITEMS.length} sections enabled
              {allSelected && <span className="ml-1 text-green-600">(full access)</span>}
            </span>
            <div className="flex gap-2">
              <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={selectAll}>
                Select All
              </Button>
              <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={deselectAll}>
                Deselect All
              </Button>
            </div>
          </div>

          <div className="space-y-2">
            {GROUPS.map(group => {
              const groupItems = MENU_ITEMS.filter(i => i.group === group);
              const checkedCount = groupItems.filter(i => selected.has(i.id)).length;
              const allChecked = checkedCount === groupItems.length;
              const someChecked = checkedCount > 0;
              const isOpen = openGroups.has(group);

              return (
                <Collapsible
                  key={group}
                  open={isOpen}
                  onOpenChange={(next) => setGroupOpen(group, next)}
                  className="group/collapsible rounded-lg border border-[#EAECF0]"
                >
                  <div className="flex items-center gap-2 px-3 py-2.5">
                    <Checkbox
                      checked={allChecked}
                      ref={(el) => {
                        if (el) (el as any).indeterminate = !allChecked && someChecked;
                      }}
                      onCheckedChange={() => toggleGroup(group)}
                      aria-label={`Grant every section in ${group}`}
                      data-testid={`perm-group-checkbox-${group}`}
                    />
                    <CollapsibleTrigger
                      className="flex flex-1 items-center gap-2 text-left"
                      data-testid={`perm-group-trigger-${group}`}
                    >
                      <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                        {group}
                      </span>
                      <span
                        className="text-[11px] tabular-nums text-muted-foreground"
                        data-testid={`perm-group-count-${group}`}
                      >
                        {checkedCount}/{groupItems.length}
                      </span>
                      <ChevronRight className="ml-auto h-4 w-4 shrink-0 text-muted-foreground transition-transform duration-200 group-data-[state=open]/collapsible:rotate-90" />
                    </CollapsibleTrigger>
                  </div>
                  <CollapsibleContent>
                    <div className="space-y-2 px-3 pb-3 pl-9">
                      {groupItems.map(item => (
                        <div key={item.id} className="flex items-center gap-2">
                          <Checkbox
                            id={item.id}
                            checked={selected.has(item.id)}
                            onCheckedChange={() => toggle(item.id)}
                          />
                          <Label htmlFor={item.id} className="cursor-pointer text-sm">
                            {item.label}
                          </Label>
                        </div>
                      ))}
                    </div>
                  </CollapsibleContent>
                </Collapsible>
              );
            })}
          </div>
      </FloatingDrawerBody>

      <FloatingDrawerFooter>
        <Button
          variant="outline"
          className="rounded-[8px]"
          onClick={() => handleOpenChange(false)}
          disabled={saving}
        >
          Cancel
        </Button>
        <Button onClick={handleSave} disabled={saving}>
          {saving ? 'Saving…' : 'Save Permissions'}
        </Button>
      </FloatingDrawerFooter>
    </FloatingDrawer>
  );
};
